import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  RoutingSmartOverviewResponse,
  RoutingSmartResetResponse,
  RoutingSmartSettingsPatch,
  RoutingSmartSettingsResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  getRoutingSmartOverview,
  getRoutingSmartSettings,
  putRoutingSmartSettings,
  resetRoutingSmart,
} from './routingSmart.ts';

export const ROUTING_SMART_KEY = ['routing-smart'] as const;

export function routingSmartSettingsKey(
  skuId: string,
): readonly ['routing-smart', 'settings', string] {
  return [...ROUTING_SMART_KEY, 'settings', skuId];
}

export function routingSmartOverviewKey(
  skuId: string,
): readonly ['routing-smart', 'overview', string] {
  return [...ROUTING_SMART_KEY, 'overview', skuId];
}

export const ROUTING_SMART_OVERVIEW_INTERVAL_MS = 10_000;
export const ROUTING_SMART_OVERVIEW_ERROR_BASE_MS = 20_000;
export const ROUTING_SMART_OVERVIEW_ERROR_MAX_MS = 80_000;

export function routingSmartOverviewRefetchInterval(query: {
  readonly state: { readonly status: string; readonly fetchFailureCount: number };
}): number {
  if (query.state.status === 'error') {
    const exp = Math.max(0, query.state.fetchFailureCount - 1);
    return Math.min(ROUTING_SMART_OVERVIEW_ERROR_BASE_MS * (2 ** exp), ROUTING_SMART_OVERVIEW_ERROR_MAX_MS);
  }
  return ROUTING_SMART_OVERVIEW_INTERVAL_MS;
}

export function useRoutingSmartSettings(
  skuId: string,
): UseQueryResult<RoutingSmartSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: routingSmartSettingsKey(skuId),
    queryFn: ({ signal }) => getRoutingSmartSettings(skuId, fetchImpl, signal),
    enabled: skuId.length > 0,
  });
}

export function useRoutingSmartOverview(
  skuId: string,
  enabled: boolean,
): UseQueryResult<RoutingSmartOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: routingSmartOverviewKey(skuId),
    queryFn: ({ signal }) => getRoutingSmartOverview(skuId, fetchImpl, signal),
    enabled: enabled && skuId.length > 0,
    refetchInterval: routingSmartOverviewRefetchInterval,
  });
}

export function usePutRoutingSmartSettings(): UseMutationResult<
  RoutingSmartSettingsResponse,
  Error,
  { readonly skuId: string; readonly body: RoutingSmartSettingsPatch }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }) => putRoutingSmartSettings(skuId, body, fetchImpl),
    onSuccess: (data, variables) => {
      qc.setQueryData(routingSmartSettingsKey(variables.skuId), data);
      void qc.invalidateQueries({ queryKey: routingSmartOverviewKey(variables.skuId) });
    },
  });
}

export function useResetRoutingSmart(): UseMutationResult<
  RoutingSmartResetResponse,
  Error,
  { readonly skuId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId }) => resetRoutingSmart(skuId, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: routingSmartOverviewKey(variables.skuId) });
    },
  });
}
